import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import setStyles from "../../setStyles";
import EditIcon from "@material-ui/icons/Edit";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@material-ui/core";
import { axiosHandler } from "../../utils/utils";
import { putUser } from "../../redux/user";

const useStyles = makeStyles((theme) => ({
  welcomeContainer: {
    display: "flex",
    width: "100%",
    textAlign: "left",
    margin: "auto",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      maxWidth: "500px",
    },
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  img: {
    width: "75px",
    height: "75px",
    borderRadius: "100%",
    backgroundColor: "#FFFFFF",
    objectFit: "cover",
  },
  profileImageContainer: {
    position: "relative",
    display: "inline-block",
  },
  editOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    borderRadius: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    opacity: 0,
    transition: "opacity 0.3s ease",
    "&:hover": {
      opacity: 1,
    },
  },
  editIcon: {
    color: "white",
  },
  fileInput: {
    display: "none",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(2),
    minWidth: 300,
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: "300px",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  },
  uploadButton: {
    marginLeft: theme.spacing(1),
  },
  dialogTitle: {
    textAlign: "center",
    borderBottom: "1px solid #f0f0f0",
    paddingBottom: theme.spacing(2),
  },
  dialogPaper: {
    borderRadius: 8,
    overflow: "hidden",
  },
  selectImageBtn: {
    padding: "10px 24px",
    fontWeight: 600,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#5662c6",
    color: "white",
    "&:hover": {
      backgroundColor: "#444fb3",
    },
  },
  cancelBtn: {
    color: "#666666",
    fontWeight: 500,
  },
  uploadBtn: {
    padding: "6px 20px",
    backgroundColor: "#4caf50",
    color: "white",
    "&:hover": {
      backgroundColor: "#3d8b40",
    },
    "&:disabled": {
      backgroundColor: "#e0e0e0",
      color: "#9e9e9e",
    },
  },
  dropzone: {
    border: "2px dashed #e0e0e0",
    borderRadius: 8,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: "100%",
    transition: "all 0.3s ease",
    backgroundColor: "#f9f9f9",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "#f5f5f5",
    },
  },
  instructions: {
    color: "#666666",
    fontSize: 14,
    marginTop: theme.spacing(1),
  },
  textContainer: {
    paddingLeft: "15px",
  },
  h2: {
    ...setStyles.sofiaProBold,
    fontSize: "36px",
    color: "white",
    [theme.breakpoints.down("xs")]: {
      fontSize: "20px",
    },
  },
  p: {
    color: "#FFFFFF",
    fontFamily: setStyles.sofiaProBold,
    fontSize: "14px",
  },
  span1: {
    color: "#A28C62",
    paddingRight: "20px",
  },
  span2: {
    paddingRight: "20px",
  },
}));

const WelcomeHeader = ({ hide }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const accessToken = useSelector((state) => state.auth?.accessToken);

  const handleEditClick = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFile(null);
    setPreviewUrl("");
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axiosHandler({
        method: "POST",
        url: "user/change-picture",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        state: {
          auth: {
            accessToken,
          },
        },
      });
      if (response?.data?.user?.profileUrl) {
        dispatch(
          putUser({
            profileUrl: response.data.user.profileUrl,
          })
        );
        handleCloseDialog();
      }
    } catch (error) {
      console.error("Error uploading profile image:", error);
      alert("Failed to upload profile image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {!hide && user ? (
        <div
          style={{
            padding: "110px 0px",
          }}
        >
          <div className={classes.welcomeContainer}>
            <div className={classes.profileImageContainer}>
              <img
                src={
                  user?.profileUrl === null
                    ? user?.team?.logoUrl
                    : user?.profileUrl
                }
                alt=""
                className={classes.img}
              />
              <div className={classes.editOverlay} onClick={handleEditClick}>
                <EditIcon className={classes.editIcon} />
              </div>
            </div>
            <div className={classes.textContainer}>
              <h2
                className={classes.h2}
              >{`${user?.firstName} ${user?.lastName}`}</h2>
              <p className={classes.p}>
                <span className={classes.span1}>
                  {user?.contract?.position?.toUpperCase()}
                </span>{" "}
                <span className={classes.span2}>
                  {`${user?.team?.city?.toUpperCase() || ""} ${
                    user?.team?.name?.toUpperCase() || ""
                  }`}
                </span>
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Dialog for uploading profile picture */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="form-dialog-title"
        PaperProps={{ className: classes.dialogPaper }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
          <Typography variant="h5" component="div">
            Update Profile Picture
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {!previewUrl ? (
            <>
              <input
                accept="image/*"
                className={classes.fileInput}
                id="profile-image-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="profile-image-upload">
                <Button className={classes.selectImageBtn} component="span">
                  SELECT IMAGE
                </Button>
              </label>
              <Typography className={classes.instructions}>
                Choose a profile photo from your device
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="subtitle1" gutterBottom>
                Preview
              </Typography>
              <Box display="flex" justifyContent="center" width="100%">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className={classes.previewImage}
                />
              </Box>
              <Typography className={classes.instructions}>
                Click Upload to confirm or select a different image
              </Typography>
              <Box mt={2}>
                <input
                  accept="image/*"
                  className={classes.fileInput}
                  id="profile-image-upload-change"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="profile-image-upload-change">
                  <Button variant="outlined" size="small" component="span">
                    Change Image
                  </Button>
                </label>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} className={classes.cancelBtn}>
            CANCEL
          </Button>
          {previewUrl && (
            <Button
              onClick={handleUpload}
              className={classes.uploadBtn}
              disabled={!selectedFile || uploading}
            >
              {uploading ? "UPLOADING..." : "UPLOAD"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WelcomeHeader;
